import { styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Text, Tooltip } from 'shell/components';

const getCourseStatus = (course) => {
    const value = course?.api_status || course?.apiStatus || course?.sync_status || course?.roster_sync_status;
    return !['failed', 'error', 'disconnected', 'offline', 'inactive'].includes(
        String(value || 'connected').toLowerCase()
    );
};

const CourseSummary = ({ course, expanded }) => {
    const { t } = useTranslation();
    const name = course?.name || course?.title || t('nav.none', 'None');
    const apiHealthy = getCourseStatus(course);
    const initials = name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase();

    return (
        <CourseSummaryRoot expanded={expanded}>
            <Text color={'light'} variant={'p3'} sx={{ whiteSpace: 'nowrap' }}>
                {expanded ? t('nav.current-course', 'Current Course') : t('nav.current', 'Current')}
            </Text>
            {course ? (
                <CourseRow expanded={expanded}>
                    <CourseBadge>{initials || 'CO'}</CourseBadge>
                    {expanded && (
                        <>
                            <CourseNameTooltip expanded={expanded}>
                                <Tooltip placement={'right'} title={name}>
                                    <CourseName noWrap variant={'p3'} weight={'medium'}>
                                        {name}
                                    </CourseName>
                                </Tooltip>
                            </CourseNameTooltip>
                            <CourseStatusTooltip expanded={expanded}>
                                <Tooltip
                                    placement={'right'}
                                    title={
                                        apiHealthy
                                            ? t('nav.api-connected', 'API Status: Connected')
                                            : t('nav.api-unavailable', 'API Status: Disconnected')
                                    }
                                >
                                    <ApiStatusDot
                                        aria-label={
                                            apiHealthy
                                                ? t('nav.api-connected', 'API Status: Connected')
                                                : t('nav.api-unavailable', 'API Status: Disconnected')
                                        }
                                        healthy={apiHealthy}
                                    />
                                </Tooltip>
                            </CourseStatusTooltip>
                        </>
                    )}
                </CourseRow>
            ) : (
                <Text noWrap variant={'p3'} weight={'medium'}>
                    {t('nav.none', 'None')}
                </Text>
            )}
        </CourseSummaryRoot>
    );
};

const CourseSummaryRoot = styled(Stack)(({ expanded, theme }) => ({
    flexShrink: 0,
    width: `calc(100% - ${theme.spacing(4)})`,
    padding: theme.spacing(2),
    gap: theme.spacing(2.5),
    alignItems: expanded ? 'flex-start' : 'center',
    borderRadius: 8,
    background: theme.palette.ensightful.neutral[50],
}));

const CourseRow = styled(Stack)(({ expanded, theme }) => ({
    width: '100%',
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: expanded ? theme.spacing(2) : 0,
    justifyContent: expanded ? 'flex-start' : 'center',
}));

const CourseBadge = styled(Box)(({ theme }) => ({
    width: 32,
    minWidth: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    background: theme.palette.ensightful.secondary[100],
    color: theme.palette.ensightful.secondary[900],
    fontSize: 12,
    fontWeight: 500,
}));

const CourseNameTooltip = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'expanded',
})(({ expanded }) => ({
    minWidth: 0,
    flex: expanded ? '1 1 auto' : '0 1 0',
    maxWidth: expanded ? '100%' : 0,
    display: 'block',
    overflow: 'hidden',
    opacity: expanded ? 1 : 0,
    visibility: expanded ? 'visible' : 'hidden',
    pointerEvents: expanded ? 'auto' : 'none',
    transition: 'max-width 160ms ease, opacity 80ms ease, visibility 0s linear',
    transitionDelay: expanded ? '0ms, 100ms, 100ms' : '0ms, 0ms, 0ms',
}));
const CourseStatusTooltip = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'expanded',
})(({ expanded }) => ({
    flex: '0 0 auto',
    width: expanded ? 8 : 0,
    overflow: 'hidden',
    opacity: expanded ? 1 : 0,
    visibility: expanded ? 'visible' : 'hidden',
    pointerEvents: expanded ? 'auto' : 'none',
    transition: 'width 160ms ease, opacity 80ms ease, visibility 0s linear',
    transitionDelay: expanded ? '0ms, 100ms, 100ms' : '0ms, 0ms, 0ms',
}));
const CourseName = styled(Text)({
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});
const ApiStatusDot = styled(Box)(({ healthy, theme }) => ({
    width: 8,
    height: 8,
    flexShrink: 0,
    borderRadius: '50%',
    background: healthy ? theme.palette.ensightful.success[600] : theme.palette.ensightful.error[400],
}));

export default CourseSummary;
